import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import NowName from '../components/shared/NowName';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel, detailValue, CPD_STATUS_META } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import CertificateReadingCard from '../components/workflow/CertificateReadingCard';
import CpdFlagsCard from '../components/workflow/CpdFlagsCard';
import { cpdRowError } from '../components/workflow/CpdCertificateRows';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// External CPD declaration — detail page (docs/modules/workflow-ld-cpd.md).
// A declaration files its CPD record on submission; nobody approves it. HR
// (ld:cpd:review) spot-checks the flags it filed with and may return it —
// the record is withdrawn, the declarant corrects and re-submits, or deletes.
// The declarant may also withdraw a filed declaration until HR has confirmed
// one of its flags.
//
// 同一組 key 有三個用途：History 的 diff 表、ChangesSinceReturnCard，以及
// 退件的逐項意見——DetailItem 的 `field=` 對到這裡的 key，意見才貼得
// 到那一格上。files 由 SHARED_CHANGE_LABELS 提供（'Attachments'）。
const CHANGE_LABELS = {
  certificateDate: 'Certificate Date',
  trainingTitle: 'Course Title',
  hours: 'Hours',
  remarks: 'Remarks',
  // The repository diffs the category by title, never by id.
  categoryTitle: 'Category',
  files: 'Certificates',
};

const fmtChangeValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
};

// History wording for this flow: 'approved' never appears (a filing is
// logged as `filed`), and the spot-check leaves `flags_confirmed`.
const ACTION_LABELS = {
  filed: ['Filed — External CPD record created', 'bg-green-50 text-green-700'],
  flags_confirmed: ['Spot-check flags confirmed', 'bg-slate-100 text-slate-600'],
  record_created: ['External CPD record created', 'bg-green-50 text-green-700'],
};

const WorkflowCpdDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [abilities, setAbilities] = useState({});
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Live probe for a RETURNED declaration: what a re-submission would collide
  // with. A filed one carries its findings in `flags`.
  const [duplicateWarning, setDuplicateWarning] = useState([]);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/ld/cpd-requests/${id}`),
        fetchJSON(`/api/ld/cpd-requests/${id}/events`),
      ]);
      setRequest(detail.request);
      setDuplicateWarning(detail.duplicateWarning || []);
      setAbilities({
        canReview: detail.canReview,
        canReturn: detail.canReturn,
        canConfirm: detail.canConfirm,
        canModify: detail.canModify,
        canResubmit: detail.canResubmit,
        canDelete: detail.canDelete,
      });
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/cpd-requests/${id}/resubmit`, { method: 'POST' });
      setNotice('Declaration re-submitted — the hours are back on your training record.');
      await load();
    } catch (e) {
      // An exact duplicate has nowhere to go: the message quotes what it
      // collided with; reload first so the banner shows it, then say so.
      await load();
      setError(e.message);
    }
    finally { setActing(false); }
  };

  const confirmFlags = async (codes) => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/cpd-requests/${id}/flags/confirm`, {
        method: 'POST',
        body: JSON.stringify({ codes }),
      });
      setNotice(codes.length === 1 ? 'Flag confirmed.' : 'All flags confirmed.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  // A returned declaration sits at 'awaiting_info' — the declarant can save
  // edits (Modify) and/or press Re-submit to file it again.
  const hasAwaitingInfo = request && request.status === 'awaiting_info';

  // 逐項意見：HR 在已建檔時可以逐格留言，申請者在被退回時讀得到。
  const fc = useFieldComments({
    events,
    canComment: !!abilities.canReturn,
    returned: !!hasAwaitingInfo,
    labels: CHANGE_LABELS,
  });

  const declared = useMemo(() => (request ? {
    trainingTitle: request.trainingTitle,
    certificateDate: request.certificateDate,
    hours: request.hours,
  } : null), [request]);

  return (
    <RequestDetailShell
      backTo="/workflow/cpd"
      backLabel="External CPD"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="External CPD"
            badge={<WorkflowStatusBadge status={request.status} meta={CPD_STATUS_META} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          {/* Only while returned: what re-submitting would collide with. An
              EXACT twin (same course, date and hours) is refused outright —
              the way out is to correct the title or delete the declaration. */}
          {duplicateWarning.length > 0 && (() => {
            const exact = duplicateWarning.filter((d) => d.exact);
            const partial = duplicateWarning.filter((d) => !d.exact);
            const entry = (d) => (
              <li key={`${d.kind}-${d.id}`}>
                {d.trainingTitle} — {d.hours}h ({formatDate(d.certificateDate)}){' '}
                {d.kind === 'request' ? (
                  <RefLink to={`/workflow/cpd/${d.id}`} className="font-medium underline hover:no-underline">
                    declaration {d.requestNo || `#${d.id}`}
                  </RefLink>
                ) : (
                  <span className="font-medium">External CPD record</span>
                )}
              </li>
            );
            return (
              <>
                {exact.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <p className="font-medium">
                      Already on file — {request.staffNameNow || request.staffName} has the same course, date and hours:
                    </p>
                    <ul className="mt-1 text-xs space-y-0.5">{exact.map(entry)}</ul>
                    <p className="mt-1 text-xs">
                      Re-submitting would book these hours twice, so it is refused. If this really is a
                      different training, give it a title that says so; otherwise delete this declaration.
                    </p>
                  </div>
                )}
                {partial.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <p className="font-medium">
                      Possible duplicate — {request.staffNameNow || request.staffName} already has the same course on this
                      date, with different hours:
                    </p>
                    <ul className="mt-1 text-xs space-y-0.5">{partial.map(entry)}</ul>
                    <p className="mt-1 text-xs">
                      Re-submitting files it with a duplicate-suspect flag for HR to check.
                    </p>
                  </div>
                )}
              </>
            );
          })()}

          <StatusActionsCard
            showDecider
            statusMeta={CPD_STATUS_META}
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onReject={abilities.canReturn ? () => setRejecting(true) : null}
            onModify={abilities.canModify ? () => setModifying(true) : null}
            onResubmit={abilities.canResubmit ? resubmit : null}
            onDelete={abilities.canDelete ? () => setConfirmingDelete(true) : null}
          >
            <div>
              <p className={detailLabel}>Hours</p>
              <p className={detailValue}>{request.hours}</p>
              {request.status === 'approved' && (
                <p className="text-xs text-green-600 mt-1">On the training record as an External CPD record</p>
              )}
              {hasAwaitingInfo && (
                <p className="text-xs text-red-600 mt-1">Withdrawn from the training record</p>
              )}
            </div>
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          <SectionCard title="Declaration Details">
            <DetailGrid>
              <DetailItem label="Staff"><NowName value={request.staffNameNow || request.staffName} now={request.staffNameNow} /></DetailItem>
              <DetailItem label="Course / Seminar" field="trainingTitle">{request.trainingTitle}</DetailItem>
              <DetailItem label="Category" field="categoryTitle">{request.categoryTitle || '—'}</DetailItem>
              <DetailItem label="Certificate Date" field="certificateDate">{formatDate(request.certificateDate)}</DetailItem>
              <DetailItem label="Hours" field="hours">{request.hours}</DetailItem>
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          {/* The spot-check findings this declaration filed with. Shown to the
              declarant too (they are told the reviewer sees them), confirmable
              only by the spot-check key while the declaration is filed. */}
          <CpdFlagsCard
            flags={request.flags || []}
            canConfirm={!!abilities.canConfirm}
            onConfirm={confirmFlags}
            busy={acting}
          />

          <CertificateReadingCard
            extracted={request.extracted}
            declared={declared}
            staffName={request.staffNameNow || request.staffName}
          />

          <SectionCard title="Certificates">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/ld/cpd-requests/${request.id}/files/${f.id}`}
              badgeFor={() => ['Certificate', 'bg-blue-50 text-blue-700']}
            />
            <FieldCommentAnchor field="files" label="Certificates" />
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={ACTION_LABELS}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/ld/cpd-requests/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the declarant — the External CPD record and its hours have been withdrawn.');
                load();
              }}
            />
          )}
          {modifying && (
            <CpdModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice('Changes saved. Press Re-submit to file the declaration again.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message={request.status === 'approved'
                ? 'This permanently deletes the declaration, its certificates and the External CPD record it filed — the hours come off your training record. This cannot be undone.'
                : 'This permanently deletes the declaration and its certificates. This cannot be undone.'}
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/ld/cpd-requests/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/cpd');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const CpdModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [trainingTitle, setTrainingTitle] = useState(request.trainingTitle || '');
  const [certificateDate, setCertificateDate] = useState(request.certificateDate || '');
  const [hours, setHours] = useState(request.hours != null ? String(request.hours) : '');
  const [categoryId, setCategoryId] = useState(request.categoryId != null ? String(request.categoryId) : '');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo, kept } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Active categories only — but a declaration already filed under a since-
  // retired one keeps it selectable, or opening the modal would silently
  // demand a re-classification.
  useEffect(() => {
    fetchJSON('/api/ld/training-categories/options')
      .then((data) => setCategoryOptions(data.data || []))
      .catch(() => setCategoryOptions([]));
  }, []);
  const staleCategory = request.categoryId != null
    && !categoryOptions.some((c) => String(c.id) === String(request.categoryId));

  // A declaration is not valid without a certificate: every existing file
  // unticked and nothing new picked leaves the attachments empty.
  const noCertificateLeft = (request.files || []).length > 0
    && kept(request.files).length === 0 && files.length === 0;

  // Everything still empty, keyed to the control it belongs to (the same
  // `field=` keys the return comments anchor to).
  const gaps = useMemo(() => {
    const g = [];
    if (!trainingTitle.trim()) g.push({ key: 'trainingTitle', label: 'Course / Seminar Title' });
    if (!certificateDate) g.push({ key: 'certificateDate', label: 'Certificate Date' });
    if (String(hours).trim() === '') g.push({ key: 'hours', label: 'Hours' });
    if (!categoryId) g.push({ key: 'categoryTitle', label: 'Category' });
    if (noCertificateLeft) g.push({ key: 'files', label: 'Attachments' });
    return g;
  }, [trainingTitle, certificateDate, hours, categoryId, noCertificateLeft]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    // The same per-certificate rule the submission form runs (mirroring the
    // server's entry rules) — with the empty fields marked above, what is
    // left is a non-positive hours value.
    const problem = cpdRowError({ trainingTitle, certificateDate, hours, categoryId });
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('trainingTitle', trainingTitle.trim());
      form.set('certificateDate', certificateDate);
      form.set('hours', hours);
      form.set('categoryId', categoryId);
      form.set('remarks', remarks);
      files.forEach(f => form.append('files', f));
      appendTo(form);
      await fetchJSON(`/api/ld/cpd-requests/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Course / Seminar Title" field="trainingTitle" required>
          <input type="text" value={trainingTitle} onChange={e => setTrainingTitle(e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Certificate Date" field="certificateDate" required>
            <input type="date" value={certificateDate} onChange={e => setCertificateDate(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Hours" field="hours" required>
            <input type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} className={inputCls} />
          </Field>
        </div>
        <Field label="Category" field="categoryTitle" required>
          <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={inputCls}>
            <option value="">Select a category…</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
            {staleCategory && (
              <option value={request.categoryId}>{request.categoryTitle || `#${request.categoryId}`}</option>
            )}
          </select>
        </Field>
        <div>
          <EditableAttachments
            files={request.files || []}
            removeIds={removeIds}
            onToggle={toggle}
            downloadHref={(f) => `${API_BASE_URL}/api/ld/cpd-requests/${request.id}/files/${f.id}`}
            badgeFor={() => ['Certificate', 'bg-blue-50 text-blue-700']}
            label="Attachments"
          />
          <FieldCommentInline field="files" />
          {(request.files || []).length === 0 && (
            <p className="mb-2 text-xs text-slate-500">No files attached yet.</p>
          )}
          {/* A CPD declaration is not valid without a certificate — said here,
              under the file list, rather than after a round trip to the server. */}
          <FieldInvalidInline field="files" text="Required — attach a replacement certificate before saving" />
          <label className="block text-xs font-medium text-slate-500 mt-2 mb-1.5">Add More Files</label>
          <FilePicker
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={e => setFiles(Array.from(e.target.files || []))}
          />
        </div>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
        </Field>
      </FormValidationProvider>
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowCpdDetail;
